// apps/vendor/src/pages/auth/ResetPasswordPage.tsx
//
// Phase 1 Task 47 — Reset password screen (vendor portal).
//
// Per CONTEXT + SPECS / AC-08:
//   - Reads `?token=xxx` from URL.
//   - newPassword + confirmPassword form, validated client-side against
//     the policy (≥12 chars, 1 upper, 1 number, 1 special).
//   - Password requirements checklist updates live as the user types.
//   - On 200 → redirect to /login with a flash message in router state.
//   - On envelope error code `AUTH_PASSWORD_REUSED` → "Password used recently".
//   - On 401 (token used / expired) → render the "link already used or expired"
//     terminal state so the user cannot retry without re-requesting a link.

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthSplitLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { useTranslation, t as i18nT } from '../../lib/i18n/index.js';

// ---------------------------------------------------------------------------
// Password policy — mirrors @printway/zod-schemas PasswordSchema.
// Inlined here so the package does not need to be a dep of the SPA shells.
// ---------------------------------------------------------------------------

const SPECIAL_CHARS_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/;

interface PolicyCheck {
  id: string;
  /** i18n key under auth.resetPassword.rules.* */
  labelKey: string;
  test: (pw: string) => boolean;
}

const PASSWORD_RULES: readonly PolicyCheck[] = [
  { id: 'len', labelKey: 'resetPassword.rules.len', test: (pw) => pw.length >= 12 },
  {
    id: 'upper',
    labelKey: 'resetPassword.rules.upper',
    test: (pw) => /[A-Z]/.test(pw),
  },
  { id: 'num', labelKey: 'resetPassword.rules.num', test: (pw) => /[0-9]/.test(pw) },
  {
    id: 'special',
    labelKey: 'resetPassword.rules.special',
    test: (pw) => SPECIAL_CHARS_RE.test(pw),
  },
];

const ResetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(12, i18nT('auth:resetPassword.validation.min'))
      .refine((p) => /[A-Z]/.test(p), i18nT('auth:resetPassword.validation.upper'))
      .refine((p) => /[0-9]/.test(p), i18nT('auth:resetPassword.validation.digit'))
      .refine(
        (p) => SPECIAL_CHARS_RE.test(p),
        i18nT('auth:resetPassword.validation.special'),
      ),
    confirmPassword: z.string().min(1, i18nT('auth:resetPassword.validation.confirmRequired')),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: i18nT('auth:resetPassword.validation.confirmMismatch'),
    path: ['confirmPassword'],
  });
type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>;

// ---------------------------------------------------------------------------
// Envelope helpers — narrow shape returned by /api/v1/auth/reset-password.
// ---------------------------------------------------------------------------

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
}

function extractErrorCode(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error.code ?? null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ServerErrorState =
  | null
  | { kind: 'reused' }
  | { kind: 'tokenInvalid' }
  | { kind: 'generic'; message: string };

export function ResetPasswordPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [serverError, setServerError] = useState<ServerErrorState>(null);
  const [tokenMissing, setTokenMissing] = useState<boolean>(false);

  useEffect(() => {
    if (!token) setTokenMissing(true);
  }, [token]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(ResetPasswordSchema),
    mode: 'onChange',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPassword = watch('newPassword') ?? '';
  const checks = useMemo(
    () =>
      PASSWORD_RULES.map((rule) => ({
        id: rule.id,
        label: t(rule.labelKey),
        passed: rule.test(newPassword),
      })),
    [newPassword, t],
  );

  const onSubmit = async (data: ResetPasswordInput): Promise<void> => {
    setServerError(null);
    if (!token) {
      setTokenMissing(true);
      return;
    }
    try {
      const client = apiClient();
      const { response, error } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).POST('/api/v1/auth/reset-password', {
        body: { token, newPassword: data.newPassword },
      });

      if (response.ok) {
        navigate('/login', {
          replace: true,
          state: {
            flash: {
              variant: 'success' as const,
              title: t('resetPassword.successFlashTitle'),
              description: t('resetPassword.successFlashDescription'),
            },
          },
        });
        return;
      }

      // Token used / expired → terminal state per AC-08. BE returns 410 GONE
      // (password.handler: AUTH_OTP_INVALID for missing/used/expired token);
      // 401 kept for back-compat. #2356: a valid new password on an
      // expired/used link was falling through to a generic "AUTH_OTP_INVALID"
      // code string instead of the friendly "request a new link" screen.
      if (
        response.status === 410 ||
        response.status === 401 ||
        extractErrorCode(error) === 'AUTH_OTP_INVALID'
      ) {
        setServerError({ kind: 'tokenInvalid' });
        return;
      }

      const code = extractErrorCode(error);
      if (code === 'AUTH_PASSWORD_REUSED') {
        setServerError({ kind: 'reused' });
        return;
      }

      setServerError({
        kind: 'generic',
        message:
          code ?? t('resetPassword.errorFallback', { status: String(response.status) }),
      });
    } catch (err) {
      setServerError({
        kind: 'generic',
        message:
          err instanceof Error ? err.message : t('resetPassword.errorUnknown'),
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Terminal state — link missing or used/expired (no retry path here).
  // ---------------------------------------------------------------------------
  if (tokenMissing || serverError?.kind === 'tokenInvalid') {
    return (
      <AuthSplitLayout variant="marketing">
      <div className="flex flex-1 items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('resetPassword.tokenInvalidTitle')}</CardTitle>
            <CardDescription>
              {t('resetPassword.tokenInvalidBody')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/forgot-password">
              <Button className="w-full">{t('resetPassword.requestNewLink')}</Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                {t('resetPassword.backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout variant="marketing">
      <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('resetPassword.title')}</CardTitle>
          <CardDescription>
            {t('resetPassword.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormField>
              <FormLabel htmlFor="newPassword">{t('resetPassword.labelNewPassword')}</FormLabel>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...register('newPassword')}
              />
              {errors.newPassword ? (
                <FormMessage>{errors.newPassword.message}</FormMessage>
              ) : null}
            </FormField>

            <ul className="space-y-1 text-xs" aria-label={t('resetPassword.rulesAria')}>
              {checks.map((c) => (
                <li
                  key={c.id}
                  className={
                    c.passed
                      ? 'text-emerald-600'
                      : 'text-pw-text-muted'
                  }
                >
                  <span aria-hidden="true">{c.passed ? '✓' : '○'}</span>{' '}
                  {c.label}
                </li>
              ))}
            </ul>

            <FormField>
              <FormLabel htmlFor="confirmPassword">{t('resetPassword.labelConfirmPassword')}</FormLabel>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword ? (
                <FormMessage>{errors.confirmPassword.message}</FormMessage>
              ) : null}
            </FormField>

            {serverError?.kind === 'reused' ? (
              <FormMessage>
                {t('resetPassword.errorReused')}
              </FormMessage>
            ) : null}
            {serverError?.kind === 'generic' ? (
              <FormMessage>{serverError.message}</FormMessage>
            ) : null}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
            </Button>
          </Form>
        </CardContent>
      </Card>
    </div>
    </AuthSplitLayout>
  );
}

export default ResetPasswordPage;
